/**
 * PWA Install Prompt Utility
 * Captures beforeinstallprompt event and shows custom install UI after device registration.
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */

import { t } from '../i18n/config.js';

let deferredPrompt = null;

// Capture the beforeinstallprompt event (fires when PWA is installable)
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.log('📲 PWA install prompt captured');
});

// Track when app is installed
window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  localStorage.setItem('kassie_pwa_installed', 'true');
  console.log('✅ Kassie PWA installed');
});

/**
 * Check if the app is running in standalone/installed mode
 */
export function isAppInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true ||
    localStorage.getItem('kassie_pwa_installed') === 'true'
  );
}

/**
 * Check if install prompt was recently dismissed (within 7 days)
 */
function wasRecentlyDismissed() {
  const dismissedAt = localStorage.getItem('kassie_pwa_dismissed_at');
  if (!dismissedAt) return false;
  
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return (Date.now() - parseInt(dismissedAt, 10)) < sevenDays;
}

/**
 * Check if running on iOS (where beforeinstallprompt is not available)
 */
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Show the PWA install prompt modal
 * Call this after successful device registration.
 */
export function showPWAInstallPrompt() {
  // Don't show if already installed
  if (isAppInstalled()) {
    console.log('📲 App already installed, skipping prompt');
    return;
  }
  
  // Don't show if recently dismissed
  if (wasRecentlyDismissed()) {
    console.log('📲 Install prompt dismissed recently, skipping');
    return;
  }
  
  // Small delay so it doesn't conflict with page transitions
  setTimeout(() => {
    if (isIOS()) {
      showIOSInstallInstructions();
    } else if (deferredPrompt) {
      showInstallModal();
    } else {
      console.log('📲 No install prompt available (not installable or already installed)');
    }
  }, 1500);
}

/**
 * Show install modal for Android/Desktop (uses beforeinstallprompt)
 */
function showInstallModal() {
  const overlay = document.createElement('div');
  overlay.className = 'pwa-install-overlay';
  overlay.innerHTML = `
    <div class="pwa-install-modal">
      <img src="/svg/branding/KassieAvatarNoBorder.svg" alt="Kassie" class="pwa-install-avatar" />
      <h2 class="pwa-install-title">${t('child.pwa.installTitle') || 'Installeer Kassie'}</h2>
      <p class="pwa-install-description">${t('child.pwa.installDescription') || 'Installeer Kassie op hierdie toestel vir vinniger laai en offline toegang.'}</p>
      <div class="pwa-install-actions">
        <button class="pwa-install-btn pwa-install-btn--primary" id="pwaInstallAccept">
          ${t('child.pwa.install') || 'Installeer'}
        </button>
        <button class="pwa-install-btn pwa-install-btn--secondary" id="pwaInstallDismiss">
          ${t('child.pwa.later') || 'Miskien Later'}
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(overlay);
  
  // Install button — fire prompt inside the gesture, then mint unawaited
  document.getElementById('pwaInstallAccept').addEventListener('click', async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      console.log('📲 Install result:', result.outcome);
      deferredPrompt = null;
    }
    overlay.remove();
    // Mint after the prompt so a slow network can't block the install dialog
    import('./handoff.js').then(({ prepareHandoffForInstall }) =>
      prepareHandoffForInstall()
    );
  });
  
  // Dismiss button
  document.getElementById('pwaInstallDismiss').addEventListener('click', () => {
    localStorage.setItem('kassie_pwa_dismissed_at', String(Date.now()));
    overlay.remove();
  });
}

/**
 * Build the iOS "Add to Home Screen" instruction markup.
 *
 * Single source of truth — the settings screen renders the same steps, and
 * previously kept its own copy that had to be edited in tandem.
 *
 * Steps 1 and 2 carry the actual iOS glyphs rather than words alone, because
 * "the Share button" means nothing until you can see which button. They are
 * white on a translucent dark chip to match how they appear on this device:
 * the manifest theme_color (#7A5C3E) is dark, so Safari tints its toolbar with
 * it and draws the glyphs white instead of the default blue.
 *
 * @returns {string} inner HTML for a .pwa-install-modal
 */
function buildIOSInstructionsMarkup() {
  return `
    <div class="pwa-install-modal">
      <img src="/svg/branding/KassieAvatarNoBorder.svg" alt="Kassie" class="pwa-install-avatar" />
      <h2 class="pwa-install-title">${t('child.pwa.installTitle') || 'Installeer Kassie'}</h2>
      <p class="pwa-install-description">${t('child.pwa.iosInstructions') || 'Om Kassie te installeer:'}</p>
      <ol class="pwa-install-steps">
        <li>
          <span class="pwa-install-step-text">${t('child.pwa.iosStep1') || 'Tik op die Deel-knoppie'}</span>
          <span class="pwa-ios-icon">
            <img src="/svg/icons/AppleShare.svg" alt="" width="24" height="24" />
          </span>
        </li>
        <li>
          <span class="pwa-install-step-text">${t('child.pwa.iosStep2') || 'Kies "Voeg by Tuisskerm"'}</span>
          <span class="pwa-ios-icon">
            <img src="/svg/icons/AppleAddToHomeScreen.svg" alt="" width="24" height="24" />
          </span>
        </li>
        <li>
          <span class="pwa-install-step-text">${t('child.pwa.iosStep3') || 'Tik "Voeg by"'}</span>
        </li>
      </ol>
      <div class="pwa-install-actions">
        <button class="pwa-install-btn pwa-install-btn--secondary" id="pwaIOSDismiss">
          ${t('child.pwa.understood') || 'Verstaan'}
        </button>
      </div>
    </div>
  `;
}

/**
 * Show iOS-specific install instructions (no beforeinstallprompt on iOS).
 *
 * @param {Object} [options]
 * @param {boolean} [options.rememberDismissal=true] - Whether dismissing should
 *   suppress the automatic prompt for 7 days. False when the parent opened the
 *   instructions deliberately from settings, since that is not a dismissal of
 *   an unsolicited prompt.
 */
export function showIOSInstallInstructions({ rememberDismissal = true } = {}) {
  const overlay = document.createElement('div');
  overlay.className = 'pwa-install-overlay';
  overlay.innerHTML = buildIOSInstructionsMarkup();

  document.body.appendChild(overlay);

  // Mint a fresh handoff token while the parent reads the instructions.
  // Unawaited: a slow or failed mint must never block the install flow.
  import('./handoff.js').then(({ prepareHandoffForInstall }) =>
    prepareHandoffForInstall()
  );

  overlay.querySelector('#pwaIOSDismiss').addEventListener('click', () => {
    if (rememberDismissal) {
      localStorage.setItem('kassie_pwa_dismissed_at', String(Date.now()));
    }
    overlay.remove();
  });
}

/**
 * Get the deferred prompt (for settings screen install button)
 */
export function getDeferredPrompt() {
  return deferredPrompt;
}
